/**
 * Friends + friend-requests store, backed by the real social API
 * (`/api/social`). State is shared across users: a request you send actually
 * reaches the other player, who can accept/decline, and both directories stay
 * in sync.
 *
 * The store is a token-aware singleton: `setAuth(token, wallet)` (called from
 * App when auth resolves) kicks off a fetch + a 20s poll so incoming requests
 * surface in the notifications bell without a refresh. Mutations apply an
 * optimistic update for snappiness, then reconcile with the server's returned
 * state. The exported shape (useFriends + actions) is unchanged from the old
 * local mock, so the panels didn't need rewriting.
 */

import { useEffect, useState } from "react";
import {
  socialApi,
  matchInvitesApi,
  type SocialState,
  type SocialUser,
  type SocialMatchInvite,
  type InviteGame,
  type InviteStatus,
  type MatchCheckIn,
  type StaffMessage,
} from "../auth/api";

export interface Friend {
  address: string;
  handle: string;
  addedAt: number;
  /** Uploaded-avatar version (0 = none → generated). */
  avatarVersion: number;
}

export interface FriendRequest {
  /** Stable id = the counterparty wallet (lowercased). */
  id: string;
  fromAddress: string;
  fromHandle: string;
  toAddress: string;
  toHandle: string;
  ts: number;
  /** Counterparty's uploaded-avatar version (0 = none). */
  avatarVersion: number;
}

export interface DiscoverableUser {
  address: string;
  handle: string;
  online: boolean;
  avatarVersion: number;
}

/** A custom-match challenge to/from a friend. */
export interface MatchInvite {
  id: string;
  /** Counterparty wallet (sender for incoming, recipient for outgoing). */
  address: string;
  handle: string;
  avatarVersion: number;
  game: InviteGame;
  stake: number;
  status: InviteStatus;
  /** `cm:<id>` once accepted — the private-room pairing key. */
  customMatchId: string | null;
  ts: number;
}

interface FriendsState {
  friends: Friend[];
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
  /** Incoming custom-match challenges (render in the notifications bell). */
  matchInvitesIn: MatchInvite[];
  /** The caller's outgoing challenges (pending + freshly accepted). */
  matchInvitesOut: MatchInvite[];
  /** Staff messages addressed to the caller (notification bell). */
  staffMessages: StaffMessage[];
}

const EMPTY: FriendsState = {
  friends: [],
  incoming: [],
  outgoing: [],
  matchInvitesIn: [],
  matchInvitesOut: [],
  staffMessages: [],
};
const POLL_MS = 20_000;
/** Tighter cadence while a challenge is outstanding so both players get
 *  pulled into the match within a few seconds of accept. */
const FAST_POLL_MS = 3_000;

let _state: FriendsState = EMPTY;
let _token: string | null = null;
let _wallet: string | null = null;
let _pollTimer: ReturnType<typeof setTimeout> | null = null;
const _subs = new Set<(s: FriendsState) => void>();

function emit() {
  for (const fn of _subs) fn(_state);
}

function handleOf(u: SocialUser): string {
  return u.handle ?? shortAddr(u.wallet);
}

function mapInvite(u: SocialMatchInvite): MatchInvite {
  return {
    id: u.id,
    address: u.wallet,
    handle: handleOf(u),
    avatarVersion: u.avatarVersion,
    game: u.game,
    stake: u.stake,
    status: u.status,
    customMatchId: u.customMatchId ?? null,
    ts: u.ts,
  };
}

/** Map the server's social state into the local UI shape. */
function mapState(s: SocialState): FriendsState {
  const me = _wallet ?? "";
  return {
    friends: s.friends.map((u) => ({
      address: u.wallet,
      handle: handleOf(u),
      addedAt: u.ts,
      avatarVersion: u.avatarVersion,
    })),
    incoming: s.incoming.map((u) => ({
      id: u.wallet,
      fromAddress: u.wallet,
      fromHandle: handleOf(u),
      toAddress: me,
      toHandle: "",
      ts: u.ts,
      avatarVersion: u.avatarVersion,
    })),
    outgoing: s.outgoing.map((u) => ({
      id: u.wallet,
      fromAddress: me,
      fromHandle: "",
      toAddress: u.wallet,
      toHandle: handleOf(u),
      ts: u.ts,
      avatarVersion: u.avatarVersion,
    })),
    matchInvitesIn: (s.matchInvitesIn ?? []).map(mapInvite),
    matchInvitesOut: (s.matchInvitesOut ?? []).map(mapInvite),
    staffMessages: (s.staffMessages ?? []).slice(),
  };
}

function applyServer(s: SocialState) {
  _state = mapState(s);
  emit();
}

async function refresh() {
  if (!_token) {
    _state = EMPTY;
    emit();
    return;
  }
  try {
    applyServer(await socialApi.get(_token));
  } catch {
    /* keep last known state on a transient failure */
  }
}

/** Poll fast only while a challenge is PENDING (a sender waiting for accept, or
 *  a recipient with an open challenge). Once accepted the sender is routed in
 *  on that same poll, so we drop back to the slow cadence rather than fast-
 *  polling for the whole match + the server's accept-route window. */
function pollDelay(): number {
  const senderWaiting = _state.matchInvitesOut.some((i) => i.status === "pending");
  const recipientPending = _state.matchInvitesIn.some((i) => i.status === "pending");
  return senderWaiting || recipientPending ? FAST_POLL_MS : POLL_MS;
}

function schedulePoll() {
  if (_pollTimer) clearTimeout(_pollTimer);
  if (!_token) return;
  _pollTimer = setTimeout(async () => {
    await refresh();
    schedulePoll();
  }, pollDelay());
}

export const friendsStore = {
  get(): FriendsState {
    return _state;
  },

  subscribe(fn: (s: FriendsState) => void) {
    _subs.add(fn);
    return () => {
      _subs.delete(fn);
    };
  },

  /** Wire the auth token + wallet. Starts/stops the background poll. */
  setAuth(token: string | null, wallet: string | null) {
    const changed = token !== _token || wallet !== _wallet;
    _token = token;
    _wallet = wallet;
    if (!token) {
      _state = EMPTY;
      emit();
      if (_pollTimer) {
        clearTimeout(_pollTimer);
        _pollTimer = null;
      }
      return;
    }
    if (changed) {
      void (async () => {
        await refresh();
        schedulePoll();
      })();
    }
  },

  refresh,

  async sendRequest(target: DiscoverableUser) {
    if (!_token) return;
    const addr = target.address.toLowerCase();
    if (addr === (_wallet ?? "").toLowerCase()) return;
    // Optimistic: show as pending immediately.
    if (!_state.outgoing.some((r) => r.toAddress.toLowerCase() === addr)) {
      _state = {
        ..._state,
        outgoing: [
          ..._state.outgoing,
          {
            id: addr,
            fromAddress: _wallet ?? "",
            fromHandle: "",
            toAddress: target.address,
            toHandle: target.handle,
            ts: Date.now(),
            avatarVersion: target.avatarVersion,
          },
        ],
      };
      emit();
    }
    try {
      applyServer(await socialApi.sendRequest(_token, target.address));
    } catch {
      void refresh();
    }
  },

  async acceptIncoming(id: string) {
    if (!_token) return;
    const req = _state.incoming.find((r) => r.id.toLowerCase() === id.toLowerCase());
    if (req) {
      _state = {
        ..._state,
        incoming: _state.incoming.filter((r) => r !== req),
        friends: [
          ..._state.friends,
          { address: req.fromAddress, handle: req.fromHandle, addedAt: Date.now(), avatarVersion: req.avatarVersion },
        ],
      };
      emit();
    }
    try {
      applyServer(await socialApi.accept(_token, id));
    } catch {
      void refresh();
    }
  },

  async declineIncoming(id: string) {
    if (!_token) return;
    _state = { ..._state, incoming: _state.incoming.filter((r) => r.id.toLowerCase() !== id.toLowerCase()) };
    emit();
    try {
      applyServer(await socialApi.decline(_token, id));
    } catch {
      void refresh();
    }
  },

  async cancelOutgoing(id: string) {
    if (!_token) return;
    _state = { ..._state, outgoing: _state.outgoing.filter((r) => r.id.toLowerCase() !== id.toLowerCase()) };
    emit();
    try {
      applyServer(await socialApi.cancel(_token, id));
    } catch {
      void refresh();
    }
  },

  async removeFriend(address: string) {
    if (!_token) return;
    _state = { ..._state, friends: _state.friends.filter((f) => f.address.toLowerCase() !== address.toLowerCase()) };
    emit();
    try {
      applyServer(await socialApi.removeFriend(_token, address));
    } catch {
      void refresh();
    }
  },

  // ── Custom match invites ──────────────────────────────────────────────

  /**
   * Challenge a friend to a custom match. Optimistic; reconciled by poll.
   * Resolves `true` when the server accepted the invite, `false` when the
   * create failed (or there's no auth token) — the ChallengeModal uses this
   * to drive its success / error result state.
   */
  async sendInvite(
    target: { address: string; handle: string; avatarVersion: number },
    game: InviteGame,
    stake: number,
  ): Promise<boolean> {
    if (!_token) return false;
    const optimistic: MatchInvite = {
      id: `pending:${target.address}:${game}:${Date.now()}`,
      address: target.address,
      handle: target.handle,
      avatarVersion: target.avatarVersion,
      game,
      stake,
      status: "pending",
      customMatchId: null,
      ts: Date.now(),
    };
    _state = {
      ..._state,
      matchInvitesOut: [
        optimistic,
        // drop any prior pending invite for the same pair+game (server supersedes too)
        ..._state.matchInvitesOut.filter(
          (i) => !(i.address.toLowerCase() === target.address.toLowerCase() && i.game === game && i.status === "pending"),
        ),
      ],
    };
    emit();
    schedulePoll(); // tighten cadence while the challenge is live
    try {
      const { invite } = await matchInvitesApi.create(_token, target.address, game, stake);
      // Reconcile the optimistic entry to the server's REAL id so a cancel
      // issued before the next poll targets the right invite (the optimistic
      // `pending:` id would 404 server-side and leave the invite alive).
      _state = {
        ..._state,
        matchInvitesOut: _state.matchInvitesOut.map((i) =>
          i.id === optimistic.id ? { ...i, id: invite.id, status: invite.status } : i,
        ),
      };
      emit();
      return true;
    } catch {
      // Create failed — drop the optimistic entry so it doesn't linger.
      _state = {
        ..._state,
        matchInvitesOut: _state.matchInvitesOut.filter((i) => i.id !== optimistic.id),
      };
      emit();
      return false;
    } finally {
      await refresh();
      schedulePoll();
    }
  },

  /** Accept an incoming challenge. Returns the check-in needed to route in. */
  async acceptInvite(id: string): Promise<MatchCheckIn | null> {
    if (!_token) return null;
    _state = { ..._state, matchInvitesIn: _state.matchInvitesIn.filter((i) => i.id !== id) };
    emit();
    try {
      const { matchCheckIn } = await matchInvitesApi.accept(_token, id);
      void refresh();
      return matchCheckIn;
    } catch {
      void refresh();
      return null;
    }
  },

  async declineInvite(id: string) {
    if (!_token) return;
    _state = { ..._state, matchInvitesIn: _state.matchInvitesIn.filter((i) => i.id !== id) };
    emit();
    try {
      await matchInvitesApi.decline(_token, id);
    } catch {
      void refresh();
    }
  },

  async cancelInvite(id: string) {
    if (!_token) return;
    _state = { ..._state, matchInvitesOut: _state.matchInvitesOut.filter((i) => i.id !== id) };
    emit();
    try {
      await matchInvitesApi.cancel(_token, id);
    } catch {
      void refresh();
    }
  },

  /** Mark a staff message read (optimistic; reconciled with the server). */
  async markStaffMessageRead(id: string) {
    if (!_token) return;
    _state = {
      ..._state,
      staffMessages: _state.staffMessages.map((m) =>
        m.id === id && m.readAt === null ? { ...m, readAt: Date.now() } : m,
      ),
    };
    emit();
    try {
      applyServer(await socialApi.markStaffMessageRead(_token, id));
    } catch {
      void refresh();
    }
  },
};

/** React hook that subscribes to the store and re-renders on changes. */
export function useFriends() {
  const [snap, setSnap] = useState(_state);
  useEffect(() => friendsStore.subscribe(setSnap), []);
  return snap;
}

/** Deterministic gradient avatar for a friend/user (matches LeaderboardPage). */
export function avatarGradient(address: string) {
  const seed = parseInt(address.slice(2, 10), 16) || 0;
  const hue1 = seed % 360;
  const hue2 = (seed * 7) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 70%, 55%), hsl(${hue2}, 75%, 45%))`;
}

export function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
